import * as React from 'react';
import styled from 'styled-components';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import Href from '@sdk/components/ui/Href';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import EmptySearchResults from '@web/assets/svg/Misc/empty-search-results.svg';

const Container = styled(Column)`
  align-items: center;
`;

enum Tab {
  employees = 'employees',
  requests = 'requests',
  deactivated = 'deactivated',
}

type Props = {
  employeesCount: number;
  joinRequestsCount: number;
  deactivatedCount: number;
  activeTab: Tab;
  search: string;
  changeTab: (props: { tab: Tab }) => void;
};

const EmployeeEmptySearchResults: React.FC<React.PropsWithChildren<Props>> = ({
  employeesCount,
  joinRequestsCount,
  deactivatedCount,
  activeTab,
  search,
  changeTab,
}) => {
  if (activeTab === Tab.employees && !employeesCount && search) {
    return (
      <Container mTop={20}>
        <EmptySearchResults />

        <Row mTop={20} mLeft={-30}>
          {!joinRequestsCount && !deactivatedCount && (
            <Text14 themeColor={ThemeColorType.gray} semiBold>
              {gettext('No employee matching with this name')}
            </Text14>
          )}

          {!joinRequestsCount && Boolean(deactivatedCount) && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', deactivatedCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.deactivated })}>
                {ngettext(
                  '1 deactivated employee',
                  '%(deactivatedCount)s deactivated employees',
                  deactivatedCount,
                  {
                    deactivatedCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}

          {Boolean(joinRequestsCount) && !deactivatedCount && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', joinRequestsCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.requests })}>
                {ngettext(
                  '1 employee join request',
                  '%(joinRequestsCount)s employees join requests',
                  joinRequestsCount,
                  {
                    joinRequestsCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}

          {Boolean(joinRequestsCount) && Boolean(deactivatedCount) && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', deactivatedCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.deactivated })}>
                {ngettext(
                  '1 deactivated employee',
                  '%(deactivatedCount)s deactivated employees',
                  deactivatedCount,
                  {
                    deactivatedCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('and')}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.requests })}>
                {ngettext(
                  '1 employee join request',
                  '%(joinRequestsCount)s employees join requests',
                  joinRequestsCount,
                  {
                    joinRequestsCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}
        </Row>
      </Container>
    );
  }

  if (activeTab === Tab.requests && !joinRequestsCount && search) {
    return (
      <Container mTop={20}>
        <EmptySearchResults />

        <Row mTop={20} mLeft={-30}>
          {!employeesCount && !deactivatedCount && (
            <Text14 themeColor={ThemeColorType.gray} semiBold>
              {gettext('No employee matching with this name')}
            </Text14>
          )}

          {!employeesCount && Boolean(deactivatedCount) && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', deactivatedCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.deactivated })}>
                {ngettext(
                  '1 deactivated employee',
                  '%(deactivatedCount)s deactivated employees',
                  deactivatedCount,
                  {
                    deactivatedCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}

          {Boolean(employeesCount) && !deactivatedCount && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', employeesCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.employees })}>
                {ngettext(
                  '1 active employee',
                  '%(employeesCount)s active employees',
                  employeesCount,
                  {
                    employeesCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}

          {Boolean(employeesCount) && Boolean(deactivatedCount) && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', deactivatedCount)}&nbsp;
              </Text14>

              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.employees })}>
                {ngettext(
                  '1 active employee',
                  '%(employeesCount)s active employees',
                  employeesCount,
                  {
                    employeesCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('and')}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.deactivated })}>
                {ngettext(
                  '1 deactivated employee',
                  '%(deactivatedCount)s deactivated employees',
                  deactivatedCount,
                  {
                    deactivatedCount,
                  }
                )}
              </Href>

              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}
        </Row>
      </Container>
    );
  }

  if (activeTab === Tab.deactivated && !deactivatedCount && search) {
    return (
      <Container mTop={20}>
        <EmptySearchResults />

        <Row mTop={20} mLeft={-30}>
          {!joinRequestsCount && !employeesCount && (
            <Text14 themeColor={ThemeColorType.gray} semiBold>
              {gettext('No employee matching with this name')}
            </Text14>
          )}

          {!joinRequestsCount && Boolean(employeesCount) && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', employeesCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.employees })}>
                {ngettext(
                  '1 active employee',
                  '%(employeesCount)s active employees',
                  employeesCount,
                  {
                    employeesCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}

          {Boolean(joinRequestsCount) && !employeesCount && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', joinRequestsCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.requests })}>
                {ngettext(
                  '1 employee join request',
                  '%(joinRequestsCount)s employees join requests',
                  joinRequestsCount,
                  {
                    joinRequestsCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}

          {Boolean(joinRequestsCount) && Boolean(employeesCount) && (
            <span>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                {ngettext('There is', 'There are', employeesCount)}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.employees })}>
                {ngettext(
                  '1 active employee',
                  '%(employeesCount)s active employees',
                  employeesCount,
                  {
                    employeesCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('and')}&nbsp;
              </Text14>
              <Href
                fontSize={14}
                themeColor={ThemeColorType.primary}
                semiBold
                onClick={(): void => changeTab({ tab: Tab.requests })}>
                {ngettext(
                  '1 employee join request',
                  '%(joinRequestsCount)s employees join requests',
                  joinRequestsCount,
                  {
                    joinRequestsCount,
                  }
                )}
              </Href>
              <Text14 themeColor={ThemeColorType.gray} semiBold>
                &nbsp;{gettext('matching')}
              </Text14>
            </span>
          )}
        </Row>
      </Container>
    );
  }

  return null;
};

export default EmployeeEmptySearchResults;
